import { Components } from "appworks/components/components";
import { Layers } from "appworks/graphics/layers/layers";
import { gameState } from "appworks/model/game-state";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { RandomFromArray } from "appworks/utils/math/random";
import { FowlBonusTransitionComponent } from "components/fowl-bonus-transition-component";
import { gameLayers } from "game-layers";
import { FowlPickBonusResult } from "model/results/fowl-pick-bonus-result";
import { FowlSlotBonusResult } from "model/results/fowl-slot-bonus-result";
import { FowlSoundEvents } from "setup/fowl-sound-events";
import { SlingoLadderResult } from "slingo/model/results/slingo-ladder-result";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { slotDefinition } from "slotworks/model/slot-definition";

export class FowlEnterSlotBonusState extends State {
    public onEnter(cascadeSkip?: boolean): void {
        const gameplay = gameState.getCurrentGame();
        const record = gameplay.getCurrentRecord();
        const returningFromPick = record instanceof FreespinRecord && record.hasResultOfType(FowlPickBonusResult);

        const ladderResult = gameplay.getLatestResultOfType(SlingoLadderResult);
        const reelset = slotDefinition.reelsets.get("slotbonus_" + ladderResult.total);

        const slotResult = gameplay.getLatestResultOfType(FowlSlotBonusResult);
        const spinsRemaining = slotResult.totalSpins - gameplay.getFreespinsPlayed();

        new Sequence([
            () =>
                Contract.wrap(() => {
                    Components.get(FooterComponent).clear();
                }),
            () => {
                if (returningFromPick) {
                    return Contract.empty();
                }
                Services.get(SoundService).customEvent(FowlSoundEvents.slot_transition);
                return Components.get(FowlBonusTransitionComponent).in("fowl_play");
            },
            () =>
                new Parallel([
                    () =>
                        Contract.wrap(() => {
                            Layers.setLayerOrder("slotbonus");

                            uiFlags.set(UIFlag.FREE_SPINS, true);
                            uiFlags.set(UIFlag.BONUS, true);
                            uiFlags.set(UIFlag.NO_SKIP, false);

                            const matrix = Components.get(MatrixComponent);
                            if (!matrix.matrixLayer.currentSceneIs("slotbonus")) {
                                matrix.change("slotbonus", [3, 3, 3, 3, 3], [0, 0, 0, 0, 0], reelset);
                                this.setNonWinningGrid(matrix);
                            }
                        }),
                    () => gameLayers.MatrixBackground.setScene("slotbonus"),
                    () => gameLayers.MatrixForeground.setScene("slotbonus"),
                    () => gameLayers.SymbolAnimations.setScene("slotbonus"),
                    () => gameLayers.Logo.setScene("slotbonus"),
                    () => gameLayers.BonusUI.setScene("slotbonus"),
                    () => gameLayers.BackgroundPersistentAnimation.defaultScene(),
                    () => gameLayers.BackgroundIntermittentAnimation.defaultScene(),
                ]),
            () =>
                Contract.wrap(() => {
                    const spinsLeftValue = gameLayers.BonusUI.getText("spins_remaining_value");
                    spinsLeftValue.text = spinsRemaining.toString();

                    let totalWinSoFar = 0;
                    gameplay.getFamily(gameplay.getRootRecord()).forEach(record => {
                        if (record instanceof FreespinRecord) {
                            totalWinSoFar += record.cashWon;
                        }
                    });
                    if (totalWinSoFar > 0) {
                        gameLayers.BonusUI.scene.slotbonus.contents.total_win_value.text = Services.get(CurrencyService).format(totalWinSoFar);
                    }

                    gameLayers.MatrixBackground.getShape("anticipation_highlight").visible = false;

                    Services.get(SoundService).customEvent("start_bonus_music");
                    Services.get(SoundService).customEvent("end_base_music");
                }),
            () => {
                if (returningFromPick) {
                    return gameLayers.ScreenFade.defaultScene();
                }
                return Components.get(FowlBonusTransitionComponent).out("fowl_play");
            },
        ]).then(() => {
            if (spinsRemaining > 0) {
                this.complete();
            } else {
                this.error();
            }
        });
    }

    protected setNonWinningGrid(matrix: MatrixComponent) {
        matrix.jumpToGrid(
            RandomFromArray([
                [
                    ["slotbonus_L2", "slotbonus_L4", "slotbonus_L3"],
                    ["slotbonus_L4", "slotbonus_L0", "slotbonus_L4"],
                    ["slotbonus_L1", "slotbonus_L3", "slotbonus_SC"],
                    ["slotbonus_H2", "slotbonus_H1", "slotbonus_L0"],
                    ["slotbonus_L0", "slotbonus_L4", "slotbonus_L1"],
                ],
                [
                    ["slotbonus_L4", "slotbonus_L3", "slotbonus_H1"],
                    ["slotbonus_L1", "slotbonus_H2", "slotbonus_L0"],
                    ["slotbonus_L4", "slotbonus_L1", "slotbonus_L2"],
                    ["slotbonus_L3", "slotbonus_L1", "slotbonus_SC"],
                    ["slotbonus_L4", "slotbonus_WI", "slotbonus_L0"],
                ],
                [
                    ["slotbonus_H1", "slotbonus_L4", "slotbonus_L1"],
                    ["slotbonus_L4", "slotbonus_L3", "slotbonus_L0"],
                    ["slotbonus_L0", "slotbonus_L1", "slotbonus_L3"],
                    ["slotbonus_H3", "slotbonus_H1", "slotbonus_L3"],
                    ["slotbonus_L4", "slotbonus_H2", "slotbonus_H1"],
                ],
                [
                    ["slotbonus_H2", "slotbonus_H1", "slotbonus_L4"],
                    ["slotbonus_L2", "slotbonus_H3", "slotbonus_L0"],
                    ["slotbonus_L0", "slotbonus_L1", "slotbonus_WI"],
                    ["slotbonus_H2", "slotbonus_L3", "slotbonus_H3"],
                    ["slotbonus_L0", "slotbonus_L4", "slotbonus_L1"],
                ],
                [
                    ["slotbonus_L1", "slotbonus_L2", "slotbonus_L4"],
                    ["slotbonus_L3", "slotbonus_H1", "slotbonus_L4"],
                    ["slotbonus_L3", "slotbonus_SC", "slotbonus_H2"],
                    ["slotbonus_L2", "slotbonus_SC", "slotbonus_L3"],
                    ["slotbonus_L4", "slotbonus_L1", "slotbonus_L3"],
                ],
                [
                    ["slotbonus_L2", "slotbonus_SC", "slotbonus_L1"],
                    ["slotbonus_L4", "slotbonus_L1", "slotbonus_H1"],
                    ["slotbonus_H1", "slotbonus_H3", "slotbonus_L1"],
                    ["slotbonus_L3", "slotbonus_H2", "slotbonus_L0"],
                    ["slotbonus_H1", "slotbonus_L2", "slotbonus_WI"],
                ],
                [
                    ["slotbonus_L2", "slotbonus_L4", "slotbonus_L0"],
                    ["slotbonus_L2", "slotbonus_SC", "slotbonus_H3"],
                    ["slotbonus_H2", "slotbonus_L3", "slotbonus_L2"],
                    ["slotbonus_L4", "slotbonus_H2", "slotbonus_H1"],
                    ["slotbonus_L0", "slotbonus_L4", "slotbonus_L1"],
                ],
            ])
        );
    }
}
